import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const stats = [
  { numKey: 'chiffre_1_num', labelKey: 'chiffre_1_label' },
  { numKey: 'chiffre_2_num', labelKey: 'chiffre_2_label' },
  { numKey: 'chiffre_3_num', labelKey: 'chiffre_3_label' },
];

function useCountUp(end: number, duration = 2000, trigger: boolean) {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!trigger || hasRun.current) return;
    hasRun.current = true;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [trigger, end, duration]);

  return count;
}

function StatNumber({ raw, trigger }: { raw: string; trigger: boolean }) {
  const num = parseInt(raw.replace(/[^0-9]/g, ''), 10) || 0;
  const suffix = raw.replace(/[0-9]/g, '');
  const count = useCountUp(num, 2000, trigger);
  return <span>{count}{suffix}</span>;
}

export default function NosChiffres() {
  const { t } = useTranslation('apropos');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);

  return (
    <section ref={ref} id="chiffres" className="bg-slate-primary py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="font-manrope font-bold text-white text-2xl md:text-3xl lg:text-4xl">
            {t('chiffres_h2')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className={`text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${idx * 150 + 200}ms` }}
            >
              <p className="font-manrope font-extrabold text-white text-4xl md:text-5xl lg:text-6xl mb-2">
                <StatNumber raw={t(stat.numKey)} trigger={isVisible} />
              </p>
              <p className="text-white/60 text-sm md:text-base">{t(stat.labelKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}